/**
 * @file frontend/src/index.js
 * @module src
 * @component index
 *
 * Index — Frontend module.
 *
 * Responsibilities:
 *   - Render the primary UI for this surface.
 *   - Coordinate API calls and local state.
 *   - Emit user actions to parent components / context where applicable.
 *
 * Notes:
 *   - All API calls go through the shared axios client (REACT_APP_BACKEND_URL).
 *   - Side effects belong in `useEffect`; keep render pure.
 */
import React from "react";
import ReactDOM from "react-dom/client";
import { init as sentryInit } from "@sentry/react";
import "@/index.css";
import App from "@/App";
import { registerServiceWorker } from "@/lib/sw_register";

if (process.env.REACT_APP_SENTRY_DSN) {
  sentryInit({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.NODE_ENV,
    tracesSampleRate: 0.1,
    ignoreErrors: [
      'ChunkLoadError',
      'Loading chunk',
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
      'Network Error',
      'NetworkError when attempting to fetch resource',
    ],
  });
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);

// WP-1.1 — PWA scaffolding (service worker for offline service log entry).
registerServiceWorker();
